import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faSearch } from '@fortawesome/free-solid-svg-icons'
import UseChat from '../../utils/useChat'
import ConversationSkeltonBox from '../skelton/conversation-skelton-box'
import ConvserationSingle from './convseration-single'
import { useNavigate } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'
import GetUser from '../../utils/getUser'
import GetRandomMemoji from '../../utils/getRandomMemoji'
import clsx from 'clsx'

type ChatFilter = "all" | "unRead" | "unReplied"

const filters: { id: ChatFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "unRead", label: "Unread" },
    { id: "unReplied", label: "Unreplied" },
]

const ChatConversationList: React.FC = () => {
    const user = GetUser()
    const [chatFilter, setChatFilter] = useState<ChatFilter>("all")
    const { conversations, getConversations, loading, loadMoreConversations, currentConversation } = UseChat()
    const [search, setSearch] = useDebounceValue("", 500)
    const [memoji, setMemoji] = useState<any | null>(null)
    const customerCode = new URLSearchParams(window.location.search)?.get("customerCode") || null
    const navigate = useNavigate()


    useEffect(() => {
        getConversations({ query: [search && { search }, chatFilter === "unRead" && { isUnRead: true }, chatFilter === "unReplied" && { isUnReplied: true }, customerCode !== null && { customerExCode: customerCode }] })
    }, [search, chatFilter, customerCode])


    // ------------------------------- [ CONVERSATION FILTERS ] ------------------------

    const onChatFilter = (newChatFilter: ChatFilter) => {
        setChatFilter((prev) => prev === newChatFilter ? "all" : newChatFilter)
    }

    // ------------------------------- [ CONVERSATION FILTERS ] ------------------------

    const GoBack = () => {
        navigate(-1)
    }

    // ------------------------------- [ SET MEMOJI ] -----------------------------------
    useMemo(() => {
        if (!memoji) {
            const newMemoji = JSON.parse(localStorage.getItem(`user_${user?.id}`)) || GetRandomMemoji()
            setMemoji(newMemoji)
            localStorage.setItem(`user_${user?.id}`, JSON.stringify(newMemoji))
        }
    }, [memoji, user?.id])

    return (
        <div className={clsx('w-[320px] min-w-[320px] h-full bg-surface border-r border-edge flex flex-col smallTablet:w-full smallTablet:min-w-0 smallTablet:border-r-0', currentConversation && 'smallTablet:hidden')}>
            {/* ----------------- CONVERSATION LIST HEADER ----------------------- */}
            <div className='px-5 pt-5 pb-4 border-b border-edge-faint shrink-0 mobile:px-4 mobile:pt-4'>
                <div className='w-full flex items-center gap-3'>
                    <button
                        type='button'
                        onClick={GoBack}
                        title='Back'
                        className='h-8 w-8 shrink-0 rounded-lg border border-edge bg-overlay flex items-center justify-center text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200'
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className='text-[12px]' />
                    </button>

                    <div className='h-10 w-10 shrink-0 rounded-full border border-edge bg-overlay-mid flex items-center justify-center overflow-hidden'>
                        <img src={memoji} alt='Avatar' className='w-[80%]' />
                    </div>

                    <div className='min-w-0'>
                        <h2 className='text-[14px] font-bold text-text-primary truncate leading-tight'>{user?.name}</h2>
                        <p className='text-[12px] font-medium text-light-gray capitalize truncate'>{user?.type?.replace('-', ' ')}</p>
                    </div>
                </div>

                {/* ------------------- SEARCH BAR ------------------------- */}

                <div className='h-10 mt-4 px-4 rounded-full flex items-center bg-input-bg border border-input-border focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 transition-all duration-200'>
                    <FontAwesomeIcon icon={faSearch} className='text-[13px] text-light-gray mr-2.5 shrink-0' />
                    <input
                        placeholder='Search conversations'
                        className='flex-1 min-w-0 text-[13px] font-medium text-text-primary placeholder:text-light-gray outline-none border-none bg-transparent'
                        onChange={(e) => setSearch(e.target.value.toLowerCase())}
                    />
                </div>

                <div className='flex items-center gap-2 mt-3 smallTablet:flex-wrap'>
                    {filters.map((filterItem) => (
                        <button
                            key={filterItem.id}
                            type='button'
                            onClick={() => onChatFilter(filterItem.id)}
                            className={clsx(
                                "h-8 px-3.5 rounded-full text-[12px] font-semibold border transition-all duration-200",
                                chatFilter === filterItem.id
                                    ? "border-indigo-400/50 bg-active-background text-text-primary"
                                    : "border-edge text-light-gray hover:text-text-primary hover:border-indigo-400/40"
                            )}
                        >
                            {filterItem.label}
                        </button>
                    ))}
                </div>

                {/* ------------------- SEARCH BAR ------------------------- */}
            </div>
            {/* ----------------- CONVERSATION LIST HEADER ----------------------- */}


            {/* ----------------- CONVERSATIONS ---------------------------------- */}

            <div className='w-full h-full overflow-y-auto overflow-x-hidden custom-scrollbar'>

                {conversations?.totalCount ? <>
                    {/* -------------------- CONVERSATION ----------------------- */}
                    {conversations?.data?.map((item) => {
                        return (
                            <ConvserationSingle item={item} key={item.id} />
                        )
                    })}
                    {/* -------------------- CONVERSATION ----------------------- */}

                </> : !loading.isFetcchingConversations ? (
                    <p className='mt-10 px-6 text-center text-[13px] font-medium text-light-gray'>No conversations found.</p>
                ) : null}
                {
                    loading.isFetcchingConversations ? [1, 2, 3, 4, 6, 7, 8, 9, 10].map((item) => {
                        return <ConversationSkeltonBox key={item} />
                    }) : conversations?.totalCount !== conversations?.data?.length ? (
                        <div className='flex justify-center py-4'>
                            <button
                                type='button'
                                onClick={() => loadMoreConversations({ query: [] })}
                                className='h-8 px-4 rounded-full border border-edge text-[12px] font-semibold text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200'
                            >
                                Load more
                            </button>
                        </div>
                    ) : null
                }
            </div>

            {/* ----------------- CONVERSATIONS ---------------------------------- */}
        </div>
    )
}

export default ChatConversationList
